// Source : web/wp-content/themes/studiometa/src/js/components/ButtonRoll.js
// Enregistré dans app.js via : '.btn': ButtonRoll

import { Base } from '@studiometa/js-toolkit';
import { gsap, SplitText } from '../utils/gsap.js';

const STAGGER = 0.025;
const DURATION = 0.4;
const EASE = 'power3.inOut';

/**
 * Effet de « roll » au survol des boutons.
 *
 * Le label est dupliqué en deux calques superposés découpés en lettres via
 * SplitText. Au survol, le calque visible sort par le haut tandis que sa copie
 * remonte depuis le bas, lettre par lettre avec un léger décalage. Le tout est
 * masqué (`overflow: hidden`) pour ne laisser apparaître qu'une seule ligne.
 *
 * Accessibilité : seul le calque d'origine reste lisible par les lecteurs
 * d'écran (la copie est `aria-hidden`). En `prefers-reduced-motion: reduce`,
 * aucun découpage ni animation n'est appliqué, le bouton reste tel quel.
 * @augments {Base}
 */
export default class ButtonRoll extends Base {
  /**
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'ButtonRoll',
  };

  /** @type {import('gsap').MatchMedia | null} */
  mm = null;

  /** @type {gsap.core.Timeline | null} */
  tl = null;

  /** @type {SplitText[]} */
  splits = [];

  /**
   *
   */
  mounted() {
    this.mm = gsap.matchMedia();

    // Sur écran tactile, mobile ou mouvement réduit : pas d'animation au survol.
    this.mm.add(
      '(prefers-reduced-motion: no-preference) and (hover: hover) and (min-width: 1024px)',
      () => {
        document.fonts.ready.then(() => {
          if (this.$el.isConnected) {
            this.setup();
          }
        });

        return () => this.teardown();
      },
    );
  }

  /**
   * Construit la structure à deux calques et la timeline de survol.
   */
  setup() {
    const text = this.$el.textContent.trim();
    if (!text) {
      return;
    }

    // Conteneur masquant le débordement vertical du roll.
    const clip = document.createElement('span');
    clip.className = 'btn-roll';

    const top = document.createElement('span');
    top.className = 'btn-roll__layer';
    top.textContent = text;

    const bottom = document.createElement('span');
    bottom.className = 'btn-roll__layer btn-roll__layer--clone';
    bottom.textContent = text;
    bottom.setAttribute('aria-hidden', 'true');

    clip.append(top, bottom);
    this.$el.textContent = '';
    this.$el.append(clip);

    this.splits = [
      SplitText.create(top, { type: 'chars', aria: 'auto' }),
      SplitText.create(bottom, { type: 'chars', aria: 'hidden' }),
    ];

    const [topChars, bottomChars] = this.splits.map((split) => split.chars);

    // Distance de défilement = hauteur réelle du calque (padding des accents
    // compris) et non 100 % de la lettre, afin que la copie disparaisse
    // complètement, accent inclus, hors de la zone masquée.
    const distance = top.offsetHeight;

    gsap.set(bottomChars, { y: distance });

    this.tl = gsap
      .timeline({ paused: true, defaults: { duration: DURATION, ease: EASE } })
      .to(topChars, { y: -distance, stagger: STAGGER }, 0)
      .to(bottomChars, { y: 0, stagger: STAGGER }, 0);

    // Les couleurs du hover (`:hover` CSS) sont rejouées dans la timeline pour
    // suivre exactement sa durée et son ease, sur toute la longueur du roll.
    const colors = this.captureHoverColors();
    if (colors) {
      this.$el.classList.add('is-roll-active');
      this.tl.fromTo(
        this.$el,
        colors.from,
        { ...colors.to, duration: this.tl.duration(), ease: EASE },
        0,
      );
    }

    this.$el.addEventListener('mouseenter', this.onEnter);
    this.$el.addEventListener('mouseleave', this.onLeave);
    this.$el.addEventListener('focus', this.onEnter);
    this.$el.addEventListener('blur', this.onLeave);
  }

  /**
   * Relève les couleurs (fond, texte, bordure) à l'état de repos et à l'état
   * survolé, en appliquant temporairement la classe sonde `is-roll-probe`.
   * @returns {{ from: object, to: object } | null}
   */
  captureHoverColors() {
    const props = ['backgroundColor', 'color', 'borderColor'];
    const read = () => {
      const styles = getComputedStyle(this.$el);
      return Object.fromEntries(props.map((prop) => [prop, styles[prop]]));
    };

    const from = read();
    this.$el.classList.add('is-roll-probe');
    const to = read();
    this.$el.classList.remove('is-roll-probe');

    // Aucune transition de couleur (ex. bouton sans variante) : on n'ajoute rien.
    const changes = props.some((prop) => from[prop] !== to[prop]);
    return changes ? { from, to } : null;
  }

  onEnter = () => this.tl?.play();

  onLeave = () => this.tl?.reverse();

  /**
   * Démonte les écouteurs, la timeline et restitue le texte d'origine.
   */
  teardown() {
    this.$el.removeEventListener('mouseenter', this.onEnter);
    this.$el.removeEventListener('mouseleave', this.onLeave);
    this.$el.removeEventListener('focus', this.onEnter);
    this.$el.removeEventListener('blur', this.onLeave);

    this.tl?.kill();
    this.tl = null;

    const text = this.$el.textContent.trim();
    this.splits.forEach((split) => split.revert());
    this.splits = [];

    // Restitue un label propre (SplitText.revert laisse le double calque) et
    // les couleurs inline posées par GSAP, en rendant la main au CSS.
    gsap.set(this.$el, { clearProps: 'backgroundColor,color,borderColor' });
    this.$el.classList.remove('is-roll-active');
    this.$el.textContent = text;
  }

  /**
   *
   */
  destroyed() {
    this.mm?.revert();
    this.mm = null;
  }
}
